import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { TournamentForFixture, getAgeCategoryFixtures } from '../../api/fixtures/fixturesService';

interface AgeCategoryFixtureForm {
  ageCategory: string;
  startDate: string;
  tournamentType: string;
  status: string;
}

const AddAgeCategoryFixturePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tournament, setTournament] = useState<TournamentForFixture | null>(null);
  const [availableAgeCategories, setAvailableAgeCategories] = useState<string[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingFixture, setExistingFixture] = useState<any>(null);
  const [formData, setFormData] = useState<AgeCategoryFixtureForm>({
    ageCategory: '',
    startDate: '',
    tournamentType: '',
    status: 'Upcoming'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Breadcrumb items - dynamic based on edit mode
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: tournament?.name || 'Tournament', onClick: () => navigate('/fixtures/age-category', { state: { selectedTournament: tournament } }) },
    { label: isEditMode ? 'Edit Age Category Fixture' : 'Add Age Category Fixture', active: true }
  ];

  // Tournament type options
  const tournamentTypeOptions = [
    { value: 'Group + Knockout', label: 'Group + Knockout' },
    { value: 'Direct Knockout', label: 'Direct Knockout' }
  ];

  // Status options
  const statusOptions = [
    { value: 'Upcoming', label: 'Upcoming' },
    { value: 'Ongoing', label: 'Ongoing' },
    { value: 'Completed', label: 'Completed' }
  ];

  useEffect(() => {
    // Get tournament data from navigation state
    const tournamentData = location.state?.selectedTournament;
    const fixtureData = location.state?.selectedFixture;
    
    if (tournamentData) {
      setTournament(tournamentData);
      
      // Check if it's edit mode
      if (fixtureData) {
        setIsEditMode(true);
        setExistingFixture(fixtureData);
        // Pre-populate form with existing data
        setFormData({
          ageCategory: fixtureData.ageCategory,
          startDate: fixtureData.startDate,
          tournamentType: fixtureData.tournamentType,
          status: fixtureData.status
        });
        setLoading(false);
      } else {
        // Add mode - load available age categories
        loadAvailableAgeCategories(tournamentData);
      }
    } else {
      // If no tournament data, redirect back to fixtures listing
      navigate('/fixtures');
    }
  }, [location.state, navigate]);

  const loadAvailableAgeCategories = async (tournamentData: TournamentForFixture) => {
    try {
      setLoading(true);
      
      // Get existing age category fixtures
      const existingFixtures = await getAgeCategoryFixtures(tournamentData._id);
      const createdCategories = existingFixtures.map(fixture => fixture.ageCategory);
      
      // Filter out already created age categories
      const available = tournamentData.ageCategories.filter(category => !createdCategories.includes(category));
      setAvailableAgeCategories(available);
      
      // If no available categories, redirect back
      if (available.length === 0) {
        alert('All age categories for this tournament have already been created.');
        navigate('/fixtures/age-category', {
          state: { selectedTournament: tournamentData }
        });
        return;
      }
    } catch (error) {
      console.error('Error loading available age categories:', error);
      // Fallback to all categories if API fails
      setAvailableAgeCategories(tournamentData.ageCategories);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AgeCategoryFixtureForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.ageCategory || !formData.startDate || !formData.tournamentType) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In real implementation, this would save to API
      console.log(isEditMode ? 'Updating age category fixture:' : 'Saving age category fixture:', formData);
      
      // Navigate back to age category listing
      navigate('/fixtures/age-category', {
        state: {
          selectedTournament: tournament
        }
      });
    } catch (error) {
      console.error('Error saving age category fixture:', error);
      alert('Error saving fixture. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/fixtures/age-category', {
      state: {
        selectedTournament: tournament
      }
    });
  };

  const isFormValid = formData.ageCategory && formData.startDate && formData.tournamentType;

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>
          
          {/* Header - Outside white box like fixtures listing */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
              <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
                <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                  <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>{isEditMode ? 'Edit Age Category Fixture' : 'Add Age Category Fixture'}</h4>
                  <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>{isEditMode ? 'Update fixture type for age category' : 'Create fixture type for age category'}</p>
                </div>
                <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', marginTop: '0', flexWrap: 'nowrap' }}>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleBack}
                    style={{ 
                      fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '150%',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #6c757d',
                      backgroundColor: 'transparent',
                      color: '#6c757d',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      textDecoration: 'none',
                      minWidth: 'auto',
                      height: '38px'
                    }}
                  >
                    ← Back
                  </button>
                </div>
              </div>
            </div>
          </div>

          <Row>
            <Col>
              <div className="tournament-table-wrapper">
                <ShimmerLoader type="form" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        
        {/* Header - Outside white box like fixtures listing */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
            <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
              <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>{isEditMode ? 'Edit Age Category Fixture' : 'Add Age Category Fixture'}</h4>
                <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>{isEditMode ? 'Update fixture type for age category' : 'Create fixture type for age category'}</p>
              </div>
              <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', marginTop: '0', flexWrap: 'nowrap' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleBack}
                  style={{ 
                    fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: '150%',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #6c757d',
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    textDecoration: 'none',
                    minWidth: 'auto',
                    height: '38px'
                  }}
                >
                  ← Back
                </button>
              </div>
            </div>
          </div>
        </div>

        <Row>
          <Col>
            {/* Form - Inside white container */}
            <div className="tournament-table-wrapper">
              <Card>
                <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  {/* Left Column */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Age Category</Form.Label>
                      {isEditMode ? (
                        // In edit mode, show as disabled field since we can't change the age category
                        <Form.Control
                          type="text"
                          value={formData.ageCategory}
                          disabled
                          style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                        />
                      ) : (
                        // In add mode, show dropdown with available categories
                        <Select
                          options={availableAgeCategories.map(category => ({
                            value: category,
                            label: category
                          }))}
                          value={formData.ageCategory ? { value: formData.ageCategory, label: formData.ageCategory } : null}
                          onChange={(option) => handleInputChange('ageCategory', option?.value || '')}
                          placeholder="Select Age Category"
                          isSearchable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  {/* Right Column */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tournament Type</Form.Label>
                      <Select
                        options={tournamentTypeOptions}
                        value={formData.tournamentType ? { value: formData.tournamentType, label: formData.tournamentType } : null}
                        onChange={(option) => handleInputChange('tournamentType', option?.value || '')}
                        placeholder="Select Tournament Type"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Select
                        options={statusOptions}
                        value={formData.status ? { value: formData.status, label: formData.status } : null}
                        onChange={(option) => handleInputChange('status', option?.value || '')}
                        placeholder="Select Status"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="form-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!isFormValid || isSubmitting}
                    className="add-btn"
                  >
                    {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update →' : 'Add →')}
                  </Button>
                </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
);
};

export default AddAgeCategoryFixturePage;
